import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { periodsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar as CalendarIcon } from 'lucide-react';
import { formatDate, cn, isPeriodFuture, isPeriodEditable, isPeriodClosed } from '../lib/utils';

const CalendarPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [currentPeriod, setCurrentPeriod] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [periodsRes, quartersRes, currentRes] = await Promise.all([
        periodsAPI.getAll({ year: selectedYear }),
        periodsAPI.getQuarters(selectedYear),
        periodsAPI.getCurrent(),
      ]);
      setPeriods(periodsRes.data);
      setQuarters(quartersRes.data);
      setCurrentPeriod(currentRes.data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  // Tag derivado de fechas (mismo patrón que Capture.jsx), con prioridad de "Capturado".
  // No depende de period.status salvo para conservar la distinción "captured".
  const getPeriodTag = (period) => {
    // Prioridad: si ya hay resultado capturado, conservar esa distinción
    if (period.status === 'captured') {
      return { label: 'Capturado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (isPeriodFuture(period)) {
      return { label: 'Pendiente', className: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    if (isPeriodClosed(period)) {
      return { label: 'Cerrado', className: 'bg-slate-100 text-slate-600 border-slate-200' };
    }
    if (isPeriodEditable(period)) {
      return { label: 'Captura', className: 'bg-blue-50 text-blue-700 border-blue-200' };
    }
    // Fallback defensivo
    return { label: 'Pendiente', className: 'bg-amber-50 text-amber-700 border-amber-200' };
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Calendario de Periodos</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Visualiza los periodos (PER) del año agrupados por trimestre</p>
          </div>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-28 sm:w-32" data-testid="calendar-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Current Period Banner */}
        {currentPeriod && currentPeriod.year === selectedYear && (() => {
          const currentQuarter = currentPeriod.month ? Math.ceil(currentPeriod.month / 3) : 1;
          const quarterLabel = `T${currentQuarter}`;
          return (
            <Card className="bg-slate-900 text-white border-0" data-testid="current-period-card">
              <CardContent className="py-3 sm:py-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-slate-400 text-xs sm:text-sm">Periodo Activo</p>
                    <p className="text-lg sm:text-xl font-bold font-['Manrope'] truncate">
                      PER {currentPeriod.number} - {quarterLabel}
                    </p>
                    <p className="text-slate-400 text-xs sm:text-sm">
                      {formatDate(currentPeriod.start_date)} - {formatDate(currentPeriod.end_date)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* No Periods */}
        {periods.length === 0 ? (
          <Card className="py-12">
            <div className="text-center">
              <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No hay periodos configurados para {selectedYear}</p>
              {isAdmin() && (
                <p className="text-slate-400 text-sm mt-1">
                  Genera los periodos desde Configuración
                </p>
              )}
            </div>
          </Card>
        ) : (
          /* Quarters Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {quarters.map((quarter) => (
              <Card key={quarter.quarter} className="card-hover" data-testid={`quarter-card-${quarter.quarter}`}>
                <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
                  <CardTitle className="text-base sm:text-lg font-['Manrope'] flex items-center justify-between">
                    <span>{quarter.name}</span>
                    <Badge variant="outline" className="font-normal text-xs">
                      {quarter.period_count} periodos
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 px-3 sm:px-6 pb-3 sm:pb-6">
                  {quarter.periods
                    .sort((a, b) => a.number - b.number)
                    .map((period) => {
                      const isCurrent = currentPeriod?.id === period.id;
                      return (
                        <div
                          key={period.id}
                          className={cn(
                            'p-2 sm:p-3 rounded-lg border transition-colors',
                            isCurrent
                              ? 'bg-emerald-50 border-emerald-200'
                              : 'bg-slate-50 border-slate-100 hover:bg-slate-100'
                          )}
                          data-testid={`period-item-${period.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                              {isCurrent && (
                                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0" />
                              )}
                              <span className="font-medium text-slate-900 text-sm">
                                PER {period.number}
                                {period.is_special && (
                                  <span className="ml-1 text-xs text-amber-600">(E)</span>
                                )}
                              </span>
                            </div>
                            <span className={cn(
                              'px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0',
                              getPeriodTag(period).className
                            )} data-testid={`period-tag-${period.number}`}>
                              {getPeriodTag(period).label}
                            </span>
                          </div>
                          <div className="mt-1 text-xs sm:text-sm text-slate-500">
                            {formatDate(period.start_date)} - {formatDate(period.end_date)}
                          </div>
                          <div className="mt-0.5 sm:mt-1 text-xs text-slate-400 hidden sm:block">
                            Edición hasta: {formatDate(period.edit_deadline)}
                          </div>
                        </div>
                      );
                    })}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Calendar Legend */}
        <Card>
          <CardContent className="py-3 sm:py-4 px-3 sm:px-6">
            <div className="flex flex-wrap items-center gap-3 sm:gap-6">
              <span className="text-xs sm:text-sm text-slate-500 font-medium w-full sm:w-auto">Leyenda:</span>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-xs sm:text-sm text-slate-600">Activo</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">Pendiente</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">Captura</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">Capturado</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">Cerrado</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CalendarPage;
